import { extractGroundingFields, formatCitationCoveragePercent } from './intelligenceMeta';
import { buildEvidenceMixSummary, groupEvidenceCitations } from './irlEvidenceGrouping';
import { analyzeValidationNarrative } from './irlEvidenceNarrativeSignals';
import { parseIrlValidationAnswer } from './irlEvidenceValidationParse';

const VDR_SCORE_HIGH = 0.72;

export const SUPPORT_LEVELS = {
    STRONGLY_SUPPORTED: 'strongly_supported',
    PARTIALLY_SUPPORTED: 'partially_supported',
    WEAKLY_SUPPORTED: 'weakly_supported',
    CONTRADICTED: 'contradicted',
    INSUFFICIENT_EVIDENCE: 'insufficient_evidence',
    VALIDATION_INCOMPLETE: 'validation_incomplete',
    NOT_CORROBORATED: 'not_corroborated',
    NOT_SUPPORTED: 'not_supported',
    NO_VDR_EVIDENCE: 'no_vdr_evidence',
    DOES_NOT_MATCH: 'does_not_match',
    EVIDENCE_RETRIEVED: 'evidence_retrieved',
    ASSESSMENT_UNAVAILABLE: 'assessment_unavailable',
    UNKNOWN: 'unknown',
};

export const SUPPORT_LABELS = {
    [SUPPORT_LEVELS.STRONGLY_SUPPORTED]: 'Supported',
    [SUPPORT_LEVELS.PARTIALLY_SUPPORTED]: 'Partially Supported',
    [SUPPORT_LEVELS.WEAKLY_SUPPORTED]: 'Weakly Supported',
    [SUPPORT_LEVELS.CONTRADICTED]: 'Contradicted',
    [SUPPORT_LEVELS.INSUFFICIENT_EVIDENCE]: 'Insufficient Evidence',
    [SUPPORT_LEVELS.VALIDATION_INCOMPLETE]: 'Validation incomplete',
    [SUPPORT_LEVELS.NOT_CORROBORATED]: 'Not corroborated by documents',
    [SUPPORT_LEVELS.NOT_SUPPORTED]: 'Not supported',
    [SUPPORT_LEVELS.NO_VDR_EVIDENCE]: 'No VDR evidence',
    [SUPPORT_LEVELS.DOES_NOT_MATCH]: 'Does not match documentary evidence',
    [SUPPORT_LEVELS.EVIDENCE_RETRIEVED]: 'Evidence Retrieved',
    [SUPPORT_LEVELS.ASSESSMENT_UNAVAILABLE]: 'Assessment Unavailable',
    [SUPPORT_LEVELS.UNKNOWN]: 'Assessment Unavailable',
};

const SUPPORT_TONE = {
    [SUPPORT_LEVELS.STRONGLY_SUPPORTED]: 'success',
    [SUPPORT_LEVELS.PARTIALLY_SUPPORTED]: 'info',
    [SUPPORT_LEVELS.WEAKLY_SUPPORTED]: 'warning',
    [SUPPORT_LEVELS.CONTRADICTED]: 'danger',
    [SUPPORT_LEVELS.INSUFFICIENT_EVIDENCE]: 'neutral',
    [SUPPORT_LEVELS.VALIDATION_INCOMPLETE]: 'warning',
    [SUPPORT_LEVELS.NOT_CORROBORATED]: 'warning',
    [SUPPORT_LEVELS.NOT_SUPPORTED]: 'warning',
    [SUPPORT_LEVELS.NO_VDR_EVIDENCE]: 'neutral',
    [SUPPORT_LEVELS.DOES_NOT_MATCH]: 'danger',
    [SUPPORT_LEVELS.EVIDENCE_RETRIEVED]: 'info',
    [SUPPORT_LEVELS.ASSESSMENT_UNAVAILABLE]: 'neutral',
    [SUPPORT_LEVELS.UNKNOWN]: 'neutral',
};

const API_VERDICT_TO_LEVEL = {
    supported: SUPPORT_LEVELS.STRONGLY_SUPPORTED,
    partially_supported: SUPPORT_LEVELS.PARTIALLY_SUPPORTED,
    contradicted: SUPPORT_LEVELS.CONTRADICTED,
    not_supported: SUPPORT_LEVELS.NOT_SUPPORTED,
    no_vdr_evidence: SUPPORT_LEVELS.NO_VDR_EVIDENCE,
};

function resolveIrlClaimValidation(response) {
    const direct =
        response?.irl_claim_validation && typeof response.irl_claim_validation === 'object'
            ? response.irl_claim_validation
            : null;
    if (direct?.support_verdict) return direct;

    const debug = response?.retrieval_debug || {};
    if (debug.support_verdict) {
        return { ...direct, support_verdict: debug.support_verdict };
    }
    return direct;
}

function mapApiSupportVerdict(verdict) {
    const key = String(verdict || '').toLowerCase().trim();
    return API_VERDICT_TO_LEVEL[key] || null;
}

const INSUFFICIENT_ANSWER_MARKERS = [
    'insufficient evidence',
    'no permitted workstreams',
    'no evidence was retrieved',
];

function mapAlignmentStatus(status, confidence) {
    const s = String(status || '').toLowerCase().trim();
    const conf = String(confidence || '').toLowerCase().trim();

    if (s === 'aligned') return SUPPORT_LEVELS.STRONGLY_SUPPORTED;
    if (s === 'contradicted') return SUPPORT_LEVELS.CONTRADICTED;
    if (s === 'insufficient_evidence' || s === 'insufficient') return SUPPORT_LEVELS.INSUFFICIENT_EVIDENCE;
    if (s === 'partial') {
        if (conf === 'low') return SUPPORT_LEVELS.WEAKLY_SUPPORTED;
        return SUPPORT_LEVELS.PARTIALLY_SUPPORTED;
    }
    return null;
}

function mapGroundingStatus(grounding_status, unsupported_claim_count) {
    const s = String(grounding_status || '').toLowerCase().trim();
    if (['strongly_grounded', 'ok', 'grounded'].includes(s)) return SUPPORT_LEVELS.STRONGLY_SUPPORTED;
    if (['partially_grounded', 'partial'].includes(s)) return SUPPORT_LEVELS.PARTIALLY_SUPPORTED;
    if (['weakly_grounded', 'weak_coverage', 'weak'].includes(s)) return SUPPORT_LEVELS.WEAKLY_SUPPORTED;
    if (s === 'blocked_no_evidence') return SUPPORT_LEVELS.INSUFFICIENT_EVIDENCE;
    if (s === 'blocked_weak_evidence') return SUPPORT_LEVELS.WEAKLY_SUPPORTED;
    if (['unsupported_claims', 'hallucination_risk'].includes(s)) {
        return Number(unsupported_claim_count) > 0
            ? SUPPORT_LEVELS.WEAKLY_SUPPORTED
            : SUPPORT_LEVELS.INSUFFICIENT_EVIDENCE;
    }
    return null;
}

function pickConfidence(alignment, apiConfidence) {
    if (alignment?.confidence) return String(alignment.confidence);
    if (apiConfidence != null && apiConfidence !== '') {
        const n = Number(apiConfidence);
        if (Number.isFinite(n)) {
            if (n >= 0.75) return 'high';
            if (n >= 0.45) return 'medium';
            return 'low';
        }
        return String(apiConfidence);
    }
    return null;
}

function resolveAlignmentAnalysis(response) {
    const direct =
        response?.alignment_analysis && typeof response.alignment_analysis === 'object'
            ? response.alignment_analysis
            : null;
    if (direct?.alignment_status) return direct;

    const debug = response?.retrieval_debug || {};
    if (debug.alignment_status) {
        return {
            ...direct,
            alignment_status: debug.alignment_status,
            confidence: debug.alignment_confidence || direct?.confidence,
            gaps: direct?.gaps || [],
            recommended_follow_up_questions: direct?.recommended_follow_up_questions || [],
            management_position: direct?.management_position,
            vdr_evidence_summary: direct?.vdr_evidence_summary,
        };
    }
    return direct;
}

function retrievalHitCounts(response) {
    const debug = response?.retrieval_debug || {};
    const fused = Number(debug.fused_count);
    const vdr = Number(debug.vdr_hit_count);
    const irl = Number(debug.irl_hit_count);
    return {
        fused: Number.isFinite(fused) ? fused : 0,
        vdr: Number.isFinite(vdr) ? vdr : 0,
        irl: Number.isFinite(irl) ? irl : 0,
    };
}

function hasRetrievedEvidence(response) {
    const citations = response?.citations || [];
    const chunks = response?.chunks || [];
    const evidence = response?.evidence || [];
    if (citations.length > 0 || chunks.length > 0 || evidence.length > 0) return true;

    const hits = retrievalHitCounts(response);
    if (hits.fused > 0 || hits.vdr > 0 || hits.irl > 0) return true;

    const alignment = resolveAlignmentAnalysis(response);
    if (alignment?.alignment_status) return true;

    const answer = String(response?.answer || response?.final_answer || '').trim();
    if (answer.length > 60) {
        const lower = answer.toLowerCase();
        if (!INSUFFICIENT_ANSWER_MARKERS.some(m => lower.includes(m))) return true;
    }
    return false;
}

function buildSubtitle(level, source, hasEvidence, hits, narrative, irlValidation) {
    if (level === SUPPORT_LEVELS.NO_VDR_EVIDENCE) {
        const gap = irlValidation?.gaps?.[0];
        if (gap) return gap;
        if (hits.irl > 0) {
            return 'Related IRL items were retrieved but no VDR documents — claim is not documentary validated';
        }
        return 'No VDR documents were retrieved — management response is not documentary validated';
    }
    if (level === SUPPORT_LEVELS.NOT_SUPPORTED) {
        return (
            irlValidation?.gaps?.[0] ||
            'Retrieved VDR material does not substantiate the management response'
        );
    }
    if (level === SUPPORT_LEVELS.NOT_CORROBORATED) {
        if (hits.vdr === 0) {
            return (
                narrative?.reasons?.[0] ||
                'No VDR documents were retrieved — the management answer is not documentary validated'
            );
        }
        return narrative?.reasons?.[0] || 'Management response is not supported by documentary evidence in scope';
    }
    if (level === SUPPORT_LEVELS.DOES_NOT_MATCH) {
        return narrative?.reasons?.[0] || 'Retrieved documents do not substantiate this management claim';
    }
    if (level === SUPPORT_LEVELS.VALIDATION_INCOMPLETE) {
        return (
            narrative?.reasons?.[0] ||
            'The AI did not produce a validation verdict — review documentary evidence below'
        );
    }
    if (level === SUPPORT_LEVELS.EVIDENCE_RETRIEVED) {
        return 'Structured alignment unavailable — review AI validation and documentary evidence below';
    }
    if (level === SUPPORT_LEVELS.INSUFFICIENT_EVIDENCE && source === 'irl_only_retrieval') {
        return 'Only related management responses were retrieved — no VDR documents matched this claim';
    }
    if (level === SUPPORT_LEVELS.INSUFFICIENT_EVIDENCE && source === 'retrieval_hits') {
        if (hits.vdr === 0 && hits.irl > 0) {
            return 'Retrieval returned management responses only — expand VDR indexing or rephrase scope';
        }
        return 'Retrieval returned limited or weak documentary support for this claim';
    }
    if (level === SUPPORT_LEVELS.ASSESSMENT_UNAVAILABLE && hasEvidence) {
        return 'Assessment unavailable — review supporting evidence below';
    }
    if (level === SUPPORT_LEVELS.ASSESSMENT_UNAVAILABLE && source === 'none') {
        if (hits.fused === 0 && hits.vdr === 0 && hits.irl === 0) {
            return 'No evidence retrieved — check intelligence API flags, workstream scope, and VDR/IRL indexing';
        }
        return 'No structured assessment returned — check intelligence API flags and data room indexing';
    }
    return null;
}

function narrativeHasDeflection(narrative) {
    return Boolean(narrative?.reasons?.some(r => /asked for clarification/i.test(r)));
}

/** True when retrieval/API signals strong VDR material for an evidence-based diligence answer. */
export function hasHighConfidenceVdrRetrieval(response, grouped, alignment, apiConfidence) {
    const confLabel = pickConfidence(alignment, apiConfidence);
    if (confLabel === 'high') return true;

    const numeric = Number(apiConfidence);
    if (Number.isFinite(numeric) && numeric >= 0.75) return true;

    const scores = (grouped?.documentary || [])
        .map(e => e.debugScore)
        .filter(n => Number.isFinite(n));
    if (scores.length && Math.max(...scores) >= VDR_SCORE_HIGH) return true;

    const debugConf = String(response?.retrieval_debug?.confidence || '').toLowerCase();
    return debugConf === 'high';
}

function applyNarrativeOverride(level, source, narrative, vdrDocCount) {
    if (!narrative?.overridesSummary) return { level, source };

    if (narrativeHasDeflection(narrative)) {
        return { level: SUPPORT_LEVELS.VALIDATION_INCOMPLETE, source: 'ai_narrative' };
    }

    if (narrative.severity === 'none' || (vdrDocCount === 0 && narrative.negativeScore >= 2)) {
        const nextLevel =
            narrative.negativeScore >= 3 ? SUPPORT_LEVELS.DOES_NOT_MATCH : SUPPORT_LEVELS.NOT_CORROBORATED;
        return { level: nextLevel, source: 'ai_narrative' };
    }
    if (narrative.severity === 'weak' || narrative.severity === 'irl_only') {
        return { level: SUPPORT_LEVELS.WEAKLY_SUPPORTED, source: 'ai_narrative' };
    }
    return { level, source };
}

/**
 * Derive validation summary hero data from intelligence ask response.
 * Priority: alignment_analysis.alignment_status → evidence_governance.grounding_status → retrieval fallback.
 */
export function deriveIRLEvidenceSupportAssessment(response, options = {}) {
    const irlValidation = resolveIrlClaimValidation(response);
    const alignment = resolveAlignmentAnalysis(response);
    const grounding = extractGroundingFields(response?.evidence_governance, response?.retrieval_debug);
    const grouped = groupEvidenceCitations(response?.citations || [], response?.chunks || [], {
        sourceIrlId: options.sourceIrlId ?? options.irlId ?? null,
        supportVerdict: irlValidation?.support_verdict,
    });
    const evidenceMix = buildEvidenceMixSummary(grouped, response?.observation_candidates);
    const hasEvidence = hasRetrievedEvidence(response);
    const hits = retrievalHitCounts(response);
    const validationDebug = irlValidation?.retrieval_debug || {};
    if (validationDebug.vdr_hit_count != null) {
        hits.vdr = Number(validationDebug.vdr_hit_count) || 0;
    }
    if (validationDebug.irl_hit_count != null) {
        hits.irl = Number(validationDebug.irl_hit_count) || 0;
    }

    let level = mapApiSupportVerdict(irlValidation?.support_verdict);
    let source = level ? 'irl_claim_validation' : null;

    if (!level) {
        level = mapAlignmentStatus(alignment?.alignment_status, alignment?.confidence);
        source = level ? 'alignment_analysis' : null;
    }

    if (!level) {
        level = mapGroundingStatus(grounding.grounding_status, grounding.unsupported_claim_count);
        source = level ? 'grounding' : null;
    }

    const vdrDocCount =
        (grouped?.documentary?.length || 0) + (grouped?.contradictingVdr?.length || 0);
    const relatedIrlCount = grouped?.relatedIrl?.length || 0;
    const answerText = String(response?.answer || response?.final_answer || '').trim();
    const narrative = analyzeValidationNarrative(answerText, {
        vdrDocCount,
        relatedIrlCount,
        vdrHits: hits.vdr,
    });

    if (hits.vdr === 0 && (hits.irl > 0 || hits.fused > 0) && source !== 'irl_claim_validation') {
        level = SUPPORT_LEVELS.NO_VDR_EVIDENCE;
        source = 'no_vdr_hits';
    }

    if (!level && hasEvidence) {
        if (vdrDocCount > 0 && !narrative.overridesSummary) {
            level = SUPPORT_LEVELS.EVIDENCE_RETRIEVED;
            source = 'retrieval';
        } else if (relatedIrlCount > 0 && vdrDocCount === 0) {
            level = SUPPORT_LEVELS.INSUFFICIENT_EVIDENCE;
            source = 'irl_only_retrieval';
        } else if (hits.vdr > 0 && !narrative.overridesSummary) {
            level = SUPPORT_LEVELS.EVIDENCE_RETRIEVED;
            source = 'retrieval_hits';
        } else if ((hits.irl > 0 || hits.fused > 0) && vdrDocCount === 0) {
            level = SUPPORT_LEVELS.INSUFFICIENT_EVIDENCE;
            source = 'retrieval_hits';
        } else if (hasEvidence && vdrDocCount > 0) {
            level = SUPPORT_LEVELS.EVIDENCE_RETRIEVED;
            source = 'retrieval';
        } else if (vdrDocCount === 0) {
            level = SUPPORT_LEVELS.NOT_CORROBORATED;
            source = 'no_vdr_evidence';
        }
    } else if (!level) {
        level = SUPPORT_LEVELS.ASSESSMENT_UNAVAILABLE;
        source = 'none';
    }

    if (source !== 'alignment_analysis' && source !== 'irl_claim_validation') {
        const overridden = applyNarrativeOverride(level, source, narrative, vdrDocCount);
        level = overridden.level;
        source = overridden.source;
    }

    if (
        (hits.vdr === 0 || level === SUPPORT_LEVELS.NO_VDR_EVIDENCE) &&
        (level === SUPPORT_LEVELS.EVIDENCE_RETRIEVED ||
            level === SUPPORT_LEVELS.STRONGLY_SUPPORTED ||
            level === SUPPORT_LEVELS.PARTIALLY_SUPPORTED)
    ) {
        level = SUPPORT_LEVELS.NO_VDR_EVIDENCE;
        source = 'no_vdr_evidence';
    }

    if (vdrDocCount === 0 && level === SUPPORT_LEVELS.EVIDENCE_RETRIEVED) {
        level = SUPPORT_LEVELS.NO_VDR_EVIDENCE;
        source = 'no_vdr_evidence';
    }

    const citationCoverageLabel = formatCitationCoveragePercent(grounding.citation_coverage);
    const unsupportedCount = Number(grounding.unsupported_claim_count) || 0;
    const validationConfidence = irlValidation?.confidence;
    const showValidationConfidence =
        source === 'irl_claim_validation' &&
        validationConfidence != null &&
        Number.isFinite(Number(validationConfidence));
    const showApiConfidence =
        !showValidationConfidence &&
        vdrDocCount > 0 &&
        hits.vdr > 0 &&
        level !== SUPPORT_LEVELS.NO_VDR_EVIDENCE &&
        !narrative.overridesSummary &&
        source !== 'ai_narrative';

    const downgradeVdrLabels = Boolean(
        level === SUPPORT_LEVELS.NO_VDR_EVIDENCE ||
        level === SUPPORT_LEVELS.NOT_SUPPORTED ||
        hits.vdr === 0 ||
        (narrative.overridesSummary &&
            (level === SUPPORT_LEVELS.NOT_CORROBORATED ||
                level === SUPPORT_LEVELS.DOES_NOT_MATCH ||
                level === SUPPORT_LEVELS.VALIDATION_INCOMPLETE ||
                level === SUPPORT_LEVELS.INSUFFICIENT_EVIDENCE ||
                narrative.severity === 'none' ||
                narrative.severity === 'weak')),
    );

    const parsedAnswer = parseIrlValidationAnswer(answerText);
    const highConfidenceVdr = hasHighConfidenceVdrRetrieval(
        response,
        grouped,
        alignment,
        response?.confidence,
    );
    const managementMismatch =
        level === SUPPORT_LEVELS.NOT_CORROBORATED ||
        level === SUPPORT_LEVELS.DOES_NOT_MATCH ||
        level === SUPPORT_LEVELS.VALIDATION_INCOMPLETE ||
        narrative.overridesSummary;

    return {
        level,
        label: SUPPORT_LABELS[level] || SUPPORT_LABELS[SUPPORT_LEVELS.ASSESSMENT_UNAVAILABLE],
        tone: SUPPORT_TONE[level] || SUPPORT_TONE[SUPPORT_LEVELS.ASSESSMENT_UNAVAILABLE],
        subtitle: buildSubtitle(level, source, hasEvidence, hits, narrative, irlValidation),
        confidence: showValidationConfidence
            ? String(validationConfidence)
            : showApiConfidence
              ? pickConfidence(alignment, response?.confidence)
              : null,
        validationConfidence: showValidationConfidence ? Number(validationConfidence) : null,
        documentaryMatchLabel: narrative.documentaryMatchLabel,
        validationSignalLabel: narrative.validationSignalLabel,
        narrativeMismatch: narrative.overridesSummary,
        downgradeVdrLabels,
        parsedAnswer,
        highConfidenceVdr,
        showEvidenceBasedAnswer: Boolean(
            parsedAnswer.hasEvidenceBasedAnswer &&
                (parsedAnswer.evidenceAnswerConfidence === 'high' ||
                    (highConfidenceVdr &&
                        managementMismatch &&
                        parsedAnswer.evidenceAnswerConfidence !== 'low')),
        ),
        narrativeReasons: narrative.reasons,
        grounding,
        citationCoverageLabel,
        unsupportedCount,
        evidenceMix,
        alignment_status: alignment?.alignment_status ?? null,
        source,
        alignment,
        irlValidation,
        hasEvidence,
        retrievalHits: hits,
        narrative,
    };
}

/** @deprecated Use groupEvidenceCitations from irlEvidenceGrouping.js */
export function filterCitationsByRole(citations = [], roles = []) {
    const roleSet = new Set(roles.map(r => String(r).toLowerCase()));
    return citations.filter(c => roleSet.has(String(c.role || '').toLowerCase()));
}

/** @deprecated Use groupEvidenceCitations from irlEvidenceGrouping.js */
export function partitionEvidenceCitations(citations = [], chunks = []) {
    const grouped = groupEvidenceCitations(citations, chunks);
    return {
        supporting: grouped.documentary,
        contradicting: grouped.contradictingVdr,
        other: grouped.relatedIrl,
    };
}
